import React, { useCallback, useEffect, useState } from 'react';
import { finalize } from 'rxjs/operators';
import { OfflinePayment } from 'utils/api/models/billingPaymentTrack/offlinePayment';
import { URL_PATHS } from './commons/constants';
import { useHistory } from 'react-router';
import { Switch, Route, useLocation } from 'react-router-dom';
import RegisteringNewPayment from './RegisteringNewPayment';
import ManagePayments from './ManagePayments';
import { BillingPaymentTrackService } from 'services/billingPaymentTrack/billingPaymentTrack';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { ISelectedAccount } from 'views/common/AccountFilterTable';
import { AccountsService } from '../../services/accounts/accounts';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch } from 'react-redux';

interface IBillingPaymentTrackProps {
	match: {
		path: string;
	};
}

const BillingPaymentTrack = ({ match }: IBillingPaymentTrackProps) => {
	const history = useHistory();
	let location = useLocation();
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [noPayments, setNoPayments] = useState(false);
	const [payments, setPayments] = useState<OfflinePayment[]>([]);
	const dispatch = useDispatch();

	const getPaymentsFromServer = useCallback(() => {
		dispatch(showLoadingIndicator());
		return BillingPaymentTrackService.getOfflinePayments(selectedAccount?.id || null)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
				})
			)
			.subscribe((resp) => {
				setNoPayments(resp.offlinePayments.length === 0);
				setPayments(resp.offlinePayments);
			});
	}, [selectedAccount, dispatch]);

	useEffect(() => {
		const subscription = AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
			setAccounts(accounts);
		});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		const subscription = getPaymentsFromServer();

		return () => {
			subscription.unsubscribe();
		};
	}, [getPaymentsFromServer]);

	useEffect(() => {
		if (location.pathname === `/${URL_PATHS.BILLING_PAYMENT_ROOT}` && noPayments) {
			history.push(`${match.path}/${URL_PATHS.REGISTER_NEW_PAYMENT}`);
		}
	});

	return (
		<Switch>
			<Route
				path={match.path}
				exact
				render={() => (
					<ManagePayments
						payments={payments}
						match={match}
						accounts={accounts}
						selectedAccount={selectedAccount}
						setSelectedAccount={setSelectedAccount}
					/>
				)}
			/>
			<Route
				path={`${match.path}/${URL_PATHS.REGISTER_NEW_PAYMENT}`}
				render={() => <RegisteringNewPayment match={match} refreshPayments={getPaymentsFromServer} />}
			/>
		</Switch>
	);
};

export default BillingPaymentTrack;
